import { defaultBoxPageCopy } from "@loot-os/api-contract";
import type {
  BoxPageContent,
  BoxPageHeading,
  BoxPageHeadingContent,
  BoxPageOpenerContent,
} from "@loot-os/design-system/templates/box/boxPageContent";
import { boxContentsContent } from "@loot-os/design-system/templates/box/boxPageContents";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { createElement, useState } from "react";
import { BoxOpener } from "../../../components/BoxOpener.js";
import { useCurrency } from "../../../lib/currency.js";
import { useDepositHref } from "../../../lib/deposit.js";
import { boxQueryOptions } from "../../../lib/queries.js";
import { useShopper } from "../../../lib/shopper.js";
import { useTenant } from "../../../lib/tenant.js";
import { useTerminology } from "../../../lib/terminology.js";

/**
 * A box's page: GET /v1/boxes/:id for its name, price and drop table, and the app's opener in
 * the opener section's slot. The same under every template. When the box fails to load the
 * heading says why and there is nothing to open, as before the page was templated. The words are
 * the store's default copy (`defaultBoxPageCopy`); the load error, the opener's controls and its
 * refusals are the platform's.
 */
export function useBoxPageContent(): BoxPageContent {
  const { boxId } = useParams({ from: "/boxes/$boxId" });
  const { user, authPending } = useShopper();
  const { hasFeature } = useTenant();
  const terms = useTerminology();
  const currency = useCurrency();
  const copy = defaultBoxPageCopy({ terms, currencyPlural: currency.namePlural });
  const [refusal, setRefusal] = useState<string | null>(null);
  // The opener's idle hero shows the heading itself, and the heading steps aside while it's up,
  // except on a layout that keeps its heading in place (stacked), where the hero leaves it out.
  const [heroShown, setHeroShown] = useState(false);
  const depositHref = useDepositHref();

  const query = useQuery(boxQueryOptions(boxId));
  const box = query.data ?? null;
  const breadcrumb = {
    trail: [
      { label: copy.breadcrumb.home, href: "/" },
      { label: copy.breadcrumb.boxes, href: "/boxes" },
    ],
    current: box?.name ?? copy.breadcrumb.loading,
  };

  if (query.error) {
    const message =
      query.error instanceof Error ? query.error.message : `failed to load ${terms.box.singular}`;
    return { breadcrumb, heading: { status: "error", message } };
  }

  const ready: BoxPageHeading | null = box
    ? {
        name: box.name,
        price: box.priceCents,
        priceNote: copy.heading.priceNote(box.openedCount),
        creator: box.forge
          ? { label: copy.heading.creatorLabel, name: box.forge.creatorDisplayName }
          : undefined,
        version: `v${box.version}`,
        // Signed in only: the create page bounces a signed-out shopper to /login and the magic
        // link lands on /, so the box would be lost on the way.
        actions:
          user && hasFeature("battles")
            ? [
                {
                  label: copy.heading.battleWithBox,
                  href: `/battles/create?boxId=${encodeURIComponent(box.id)}`,
                  emphasis: "secondary",
                },
              ]
            : [],
      }
    : null;
  const heading: BoxPageHeadingContent = ready
    ? { status: "ready", ...ready, inHero: heroShown }
    : { status: "loading" };

  const opener: BoxPageOpenerContent = {
    slot: createElement(BoxOpener, {
      boxId,
      heading: ready,
      onRefusal: setRefusal,
      onHeroShown: setHeroShown,
    }),
    // Held back until the auth check answers, so a signed-in shopper never sees it flash.
    demo:
      !authPending && !user
        ? {
            message: copy.opener.demo.message,
            action: { label: copy.opener.demo.signIn, href: "/login", emphasis: "primary" },
          }
        : undefined,
    error: refusal ?? undefined,
    footnote:
      user && hasFeature("balancePurchases")
        ? [
            `${copy.opener.footnote.short} `,
            { text: copy.opener.footnote.buy, link: { href: depositHref } },
          ]
        : undefined,
  };

  return {
    breadcrumb,
    heading,
    opener,
    contents: boxContentsContent(box, copy.contents, currency, terms),
  };
}
